import React from 'react';
import { Moon, Sun } from 'lucide-react';
import { useThemeSettings } from '../../lib/ThemeContext';
import type { ThemeTransitionOrigin } from '../../lib/themeTransition';

type ThemeToggleProps = {
  variant?: 'switch' | 'menu';
  className?: string;
  labelLight?: string;
  labelDark?: string;
  onToggle?: () => void;
};

function originFromEvent(e: React.MouseEvent<HTMLElement>): ThemeTransitionOrigin {
  const rect = e.currentTarget.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
}

export function ThemeToggle({
  variant = 'switch',
  className = '',
  labelLight = 'Light mode',
  labelDark = 'Dark mode',
  onToggle,
}: ThemeToggleProps) {
  const { darkMode, toggleDarkMode } = useThemeSettings();

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    toggleDarkMode(originFromEvent(e));
    onToggle?.();
  };

  if (variant === 'menu') {
    return (
      <button
        type="button"
        role="switch"
        aria-checked={darkMode}
        aria-label={darkMode ? labelLight : labelDark}
        className={`theme-toggle-menu flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:text-zinc-200 dark:hover:bg-zinc-800 ${className}`}
        onClick={handleClick}
      >
        <span className="min-w-0 flex-1">{darkMode ? labelLight : labelDark}</span>
        <span className={`theme-toggle theme-toggle--sm ${darkMode ? 'is-dark' : 'is-light'}`} aria-hidden>
          <span className="theme-toggle-track">
            <span className="theme-toggle-sky" />
            <span className="theme-toggle-stars" />
          </span>
          <span className="theme-toggle-thumb">
            <Sun className="theme-toggle-icon theme-toggle-sun" strokeWidth={2.25} />
            <Moon className="theme-toggle-icon theme-toggle-moon" strokeWidth={2.25} />
          </span>
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      role="switch"
      aria-checked={darkMode}
      aria-label={darkMode ? labelLight : labelDark}
      title={darkMode ? labelLight : labelDark}
      className={`theme-toggle ${darkMode ? 'is-dark' : 'is-light'} ${className}`}
      onClick={handleClick}
    >
      <span className="theme-toggle-track">
        <span className="theme-toggle-sky" />
        <span className="theme-toggle-stars" />
      </span>
      <span className="theme-toggle-thumb">
        <Sun className="theme-toggle-icon theme-toggle-sun" strokeWidth={2.25} />
        <Moon className="theme-toggle-icon theme-toggle-moon" strokeWidth={2.25} />
      </span>
    </button>
  );
}
